import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './OrderTokenPage.css';

function OrderTokenPage() {
  const { token } = useParams();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No appointment token provided. Please check the URL.');
      setLoading(false);
      return;
    }

    const fetchAppointmentData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get(
          `https://vitaldev.vitalinsights.in/appointment/order-page/${token}`,
          {
            timeout: 10000,
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.data.status === true && response.data.code === 200) {
          setAppointments(response.data.data || []);
          setError(null);
        } else {
          setError(response.data.message || 'Failed to fetch appointment data');
          setAppointments([]);
        }
      } catch (err) {
        let errorMessage = 'Failed to load appointment data';
        
        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = 'Appointment not found. Please check the token.';
          } else if (err.response.status === 400) {
            errorMessage = 'Invalid appointment token.';
          } else if (err.response.status === 500) {
            errorMessage = 'Server error. Please try again later.';
          } else {
            errorMessage = err.response.data?.message || `Error: ${err.response.status}`;
          }
        } else if (err.request) {
          errorMessage = 'Unable to connect to the server. Please check your internet connection.';
        } else {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        setAppointments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointmentData();
  }, [token]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const dayName = date.toLocaleString('en-US', { weekday: 'short' });
    return { day, month, year, dayName };
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const initiatePayment = async () => {
    try {
      setPaymentLoading(true);
      
      const appointmentIds = appointments.map(apt => apt.id);
      
      const response = await axios.post(
        'https://vitaldev.vitalinsights.in/payment/payment-link',
        {
          appointment_id: appointmentIds
        },
        {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.payment_details && response.data.payment_details.payment_link) {
        window.open(response.data.payment_details.payment_link, '_blank');
      } else {
        setError('Failed to get payment link. Please try again.');
      }
    } catch (err) {
      let errorMessage = 'Failed to initiate payment';
      
      if (err.response) {
        errorMessage = err.response.data?.message || `Error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = 'Unable to connect to payment server. Please check your internet connection.';
      } else {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="phone-shell">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading appointment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="phone-shell">
        <div className="error-container">
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  if (!appointments || appointments.length === 0) {
    return (
      <div className="phone-shell">
        <div className="error-container">
          <p className="error-message">No appointment data found</p>
        </div>
      </div>
    );
  }

  // Separate home collection and center visit appointments
  const homeCollectionAppt = appointments.find(apt => apt.collection_type === 0);
  const centerVisitAppt = appointments.find(apt => apt.collection_type === 1);
  
  // Use the first appointment for header info
  const primaryAppt = homeCollectionAppt || centerVisitAppt || appointments[0];
  
  // Combine all tests from all appointments
  const allTests = [];
  const testMap = new Map();
  appointments.forEach(apt => {
    if (apt.availableTests) {
      apt.availableTests.forEach(test => {
        const key = test.id;
        if (!testMap.has(key)) {
          testMap.set(key, test);
          allTests.push(test);
        }
      });
    }
  });

  // Calculate total amount
  const totalAmount = appointments.reduce((sum, apt) => sum + (apt.amount || 0), 0);
  const hasPaymentId = appointments.some(apt => apt.payment_id !== null);

  // Collect all preparation instructions from all tests
  let allPreparations = [];
  const prepSet = new Set();
  appointments.forEach(apt => {
    if (apt.availableTests) {
      apt.availableTests.forEach(test => {
        if (test.preparation) {
          const prepLines = test.preparation.split('\n').filter(line => line.trim());
          prepLines.forEach(line => {
            const trimmed = line.trim();
            if (trimmed && !prepSet.has(trimmed)) {
              prepSet.add(trimmed);
              allPreparations.push(trimmed);
            }
          });
        }
      });
    }
  });

  // Fallback: Hardcoded preparation instructions if none found from API
  // if (allPreparations.length === 0) {
    allPreparations = [
      'Avoid hard exercise for 24 hours before your appointment.',
      'Wear comfortable sports clothes and running shoes, and avoid wearing any metal.',
      'Do not take calcium supplements or drink alcohol for 24 hours before your test.',
      'Do not eat for at least 4-6 hours before your test you can drink water, but not too much right before you arrive',
      'Please let us know about any injuries, illnesses, or medications you are currently taking this helps us get the best results for you.'
    ];
  // }

  const { day, month, year, dayName } = formatDate(primaryAppt.slot_start_time);
  const profileName = primaryAppt.profile?.name || 'Guest';

  return (
    <div className="phone-shell">
      {/* HERO SECTION */}
      <div className="hero-card" style={{backgroundImage: 'url(/Vector.svg)', backgroundSize: 'cover', backgroundPosition: 'center top'}}>
        <div className="hero-card-inner">
          {/* Top Bar */}
          <div className="hero-top-bar">
            <img src="/SECONDARY LOGO.svg" alt="Vital Insights" className="hero-logo-img" style={{marginLeft: '-13px'}} />
            <a href="tel:+919642396422" className="hero-call-btn">
              <svg viewBox="0 0 24 24" fill="white" width="15" height="15">
                <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.32.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.61 21 3 13.39 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.26.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z"/>
              </svg>
              Call us
            </a>
          </div>

          {/* Name + Badge */}
          <div className="hero-name-row">
            <div className="hero-name-section">
              <div className="hero-patient-name">{profileName}</div>
              <div className="hero-patient-phone">{primaryAppt.user?.phone ? primaryAppt.user.phone.replace(/^\+91/, '').replace(/(\d{5})(\d{5})/, '$1 $2') : ''}</div>
            </div>
            {(() => {
              const hasReports = appointments.some(apt => apt.report_urls && apt.report_urls.length > 0);
              const isPaid = hasPaymentId;
              
              if (hasReports) {
                return (
                  <div 
                    className="hero-badge-reports"
                    onClick={() => {
                      const reportsSection = document.getElementById('reports-section');
                      if (reportsSection) {
                        reportsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    style={{cursor: 'pointer'}}
                  >
                    <div className="hero-badge-dot-reports"></div>
                    <span className="hero-badge-text-reports">Reports Generated</span>
                  </div>
                );
              } else if (isPaid) {
                return (
                  <div className="hero-badge-confirmed">
                    <div className="hero-badge-dot"></div>
                    <span className="hero-badge-text">Booking Confirmed</span>
                  </div>
                );
              } else {
                return (
                  <div 
                    className="hero-badge-pending"
                    onClick={() => {
                      const testsSection = document.getElementById('tests-section');
                      if (testsSection) {
                        testsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    style={{cursor: 'pointer'}}
                  >
                    <div className="hero-badge-dot-pending"></div>
                    <span className="hero-badge-text-pending">Payment Pending</span>
                  </div>
                );
              }
            })()}
          </div>

          {/* Info Note */}
          <div className="hero-info-note">
            <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
              <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.6)" strokeWidth="1.8"/>
              <line x1="12" y1="8" x2="12" y2="8" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round"/>
              <line x1="12" y1="11" x2="12" y2="16" stroke="rgba(255,255,255,0.6)" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            <span className="hero-info-note-text">Provide this phone number at the branch.</span>
          </div>

          {/* Details Row */}
          <div className="hero-details-row">
            <div className="hero-detail-cell">
              <span className="hero-detail-label">Time Slot</span>
              <span className="hero-detail-value">
                {String(day).padStart(2,'0')}/{month.slice(0,3).toUpperCase()}/{String(year).slice(-2)}, {dayName.toUpperCase()}<br/>
                {formatTime(primaryAppt.slot_start_time)}
              </span>
            </div>
            <div className="hero-detail-cell">
              <span className="hero-detail-label">Branch</span>
              <span className="hero-detail-value">{centerVisitAppt?.facility?.name || primaryAppt.facility?.name}</span>
            </div>
            <a href={`https://maps.google.com/?q=${encodeURIComponent(centerVisitAppt?.facility?.name || primaryAppt.facility?.name)}`} target="_blank" rel="noopener noreferrer" className="hero-maps-cell">
              <svg className="hero-maps-icon" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              <span className="hero-maps-label">Directions</span>
            </a>
          </div>
        </div>
      </div>

      {/* ALERT BANNER - Show for home collection */}
      {homeCollectionAppt && (
        <div style={{margin:'16px 16px 0',background:'var(--amber-bg)',border:'1.5px solid #F5C842',borderRadius:'var(--r)',padding:'18px 18px',display:'flex',flexDirection:'column',gap:'14px'}}>
          <div>
            <div style={{fontSize:'16px',fontWeight:'600',color:'var(--ink)',marginBottom:'8px'}}>We Are Coming To You First!</div>
            <div style={{fontSize:'15px',color:'var(--ink-2)',lineHeight:'1.6',display:'flex',alignItems:'center',gap:'8px',marginBottom:'10px'}}>
              <span style={{fontSize:'18px'}}>🕐</span>
              <strong style={{fontSize:'15px'}}>{homeCollectionAppt.slot_object && homeCollectionAppt.slot_object[0]?.slotTime ? homeCollectionAppt.slot_object[0].slotTime : formatTime(homeCollectionAppt.slot_start_time)}</strong>
            </div>
            <div style={{fontSize:'13px',color:'var(--ink-2)',lineHeight:'1.6'}}>
              <div style={{marginBottom:'6px'}}>• Do not eat or fast at least <strong>8–12 hours</strong> before your test.</div>
              <div style={{marginBottom:'6px'}}>• If there are any location or availability changes, please call us to reschedule</div>
              <div>• Our phlebotomist will visit the following address to collect your blood sample.</div>
            </div>
          </div>
          {homeCollectionAppt.address && (
            <div style={{background:'rgba(0,0,0,0.04)',borderRadius:'var(--r-sm)',padding:'12px 14px',display:'flex',gap:'12px',alignItems:'flex-start'}}>
              <div style={{fontSize:'18px',flexShrink:'0'}}>📍</div>
              <div style={{fontSize:'13px',color:'var(--ink)',lineHeight:'1.5',fontWeight:'500'}}>
                {homeCollectionAppt.address.line1}{homeCollectionAppt.address.line2 ? `, ${homeCollectionAppt.address.line2}` : ''}<br/>
                {homeCollectionAppt.address.city}, {homeCollectionAppt.address.state} - {homeCollectionAppt.address.zip}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SECTION 1: Your Day at a Glance - COMMENTED OUT (Client may ask again) */}
      {/* 
      <div className="section">
        <div className="section-header">
          <div className="section-title">
            <div className="section-icon">🗓️</div>
            Your Day at a Glance
          </div>
        </div>
        <div className="location-body">
          {homeCollectionAppt && (
            <div style={{display:'flex', gap:'14px', marginBottom: '20px'}}>
              <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'0'}}>
                <div style={{width:'36px',height:'36px',borderRadius:'50%',background:'var(--brand)',color:'white',fontSize:'15px',display:'grid',placeItems:'center',flexShrink:'0'}}>🏠</div>
                <div style={{width:'2px',flex:'1',minHeight:'32px',background:'var(--line)',marginTop:'6px'}}></div>
              </div>
              <div style={{flex:'1',paddingBottom:'20px'}}>
                <div style={{fontFamily:"'Instrument Sans',sans-serif",fontSize:'11px',fontWeight:'700',letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--ink-3)',marginBottom:'4px'}}>Step 1 · {homeCollectionAppt.slot_object && homeCollectionAppt.slot_object[0]?.slotTime ? homeCollectionAppt.slot_object[0].slotTime : formatTime(homeCollectionAppt.slot_start_time)}</div>
                <div style={{fontFamily:"'Instrument Sans',sans-serif",fontSize:'15px',fontWeight:'600',color:'var(--ink)',marginBottom:'3px'}}>Blood Collection at Home</div>
                <div style={{fontSize:'13px',color:'var(--ink-3)',lineHeight:'1.5'}}>Our phlebotomist will come to you at<br/><strong style={{color:'var(--ink-2)'}}>{homeCollectionAppt.address ? `${homeCollectionAppt.address.line1}, ${homeCollectionAppt.address.city}, ${homeCollectionAppt.address.state}` : homeCollectionAppt.facility?.address}</strong></div>
                <div style={{marginTop:'10px',display:'inline-flex',alignItems:'center',gap:'6px',background:'var(--green-bg)',color:'var(--green)',fontSize:'12.5px',fontWeight:'600',padding:'5px 11px',borderRadius:'8px'}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  Be home &amp; fasting before arrival
                </div>
              </div>
            </div>
          )}
          {centerVisitAppt && (
            <div style={{display:'flex', gap:'14px', marginBottom: '16px'}}>
              <div style={{display:'flex', flexDirection:'column', alignItems:'center'}}>
                <div style={{width:'36px',height:'36px',borderRadius:'50%',background:'var(--ink)',color:'white',fontSize:'15px',display:'grid',placeItems:'center',flexShrink:'0'}}>🏥</div>
              </div>
              <div style={{flex:'1'}}>
                <div style={{fontFamily:"'Instrument Sans',sans-serif",fontSize:'11px',fontWeight:'700',letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--ink-3)',marginBottom:'4px'}}>Step {homeCollectionAppt ? '2' : '1'} · {formatTime(centerVisitAppt.slot_start_time)}</div>
                <div style={{fontFamily:"'Instrument Sans',sans-serif",fontSize:'15px',fontWeight:'600',color:'var(--ink)',marginBottom:'3px'}}>Visit the Branch</div>
                <div style={{fontSize:'13px',color:'var(--ink-3)',lineHeight:'1.5',marginBottom:'12px'}}><strong style={{color:'var(--ink-2)'}}>{centerVisitAppt.facility?.name}</strong></div>
                <a href={`https://maps.google.com/?q=${encodeURIComponent(centerVisitAppt.facility?.name || 'Vital Insights')}`} className="map-btn" target="_blank" rel="noopener noreferrer">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  Get Directions to Branch
                </a>
              </div>
            </div>
          )}
          {homeCollectionAppt && (
            <div style={{display:'flex',alignItems:'flex-start',gap:'10px',background:'#FFF4E0',borderRadius:'8px',padding:'11px 13px',marginTop:'4px',marginBottom:'4px'}}>
              <span style={{fontSize:'16px',flexShrink:'0'}}>📞</span>
              <div style={{fontSize:'12.5px',color:'#7A5200',lineHeight:'1.55'}}>
                If your <strong style={{color:'var(--amber)'}}>home address or availability changes</strong> for the morning collection, please call us right away so we can reschedule the visit.
              </div>
            </div>
          )}
        </div>
      </div>
      */}

      {/* SECTION 0: Before You Come In - Dynamic from test preparations */}
      {allPreparations.length > 0 && (
        <div className="section">
          <div className="section-header">
            <div className="section-title">
              <div className="section-icon">
                <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.35581 14.25C0.981812 14.25 0.662313 14.1176 0.397313 13.8527C0.132438 13.5877 0 13.2682 0 12.8942V2.85581C0 2.48181 0.132438 2.16231 0.397313 1.89731C0.662313 1.63244 0.981812 1.5 1.35581 1.5H4.69331C4.74044 1.08363 4.92219 0.729562 5.23856 0.437812C5.55481 0.145937 5.93363 0 6.375 0C6.82113 0 7.20238 0.145937 7.51875 0.437812C7.83513 0.729562 8.01444 1.08363 8.05669 1.5H11.3942C11.7682 1.5 12.0877 1.63244 12.3527 1.89731C12.6176 2.16231 12.75 2.48181 12.75 2.85581V12.8942C12.75 13.2682 12.6176 13.5877 12.3527 13.8527C12.0877 14.1176 11.7682 14.25 11.3942 14.25H1.35581ZM1.35581 13.125H11.3942C11.4519 13.125 11.5048 13.1009 11.5528 13.0528C11.6009 13.0048 11.625 12.9519 11.625 12.8942V2.85581C11.625 2.79806 11.6009 2.74519 11.5528 2.69719C11.5048 2.64906 11.4519 2.625 11.3942 2.625H1.35581C1.29806 2.625 1.24519 2.64906 1.19719 2.69719C1.14906 2.74519 1.125 2.79806 1.125 2.85581V12.8942C1.125 12.9519 1.14906 13.0048 1.19719 13.0528C1.24519 13.1009 1.29806 13.125 1.35581 13.125ZM2.8125 11.3509H7.6875V10.2261H2.8125V11.3509ZM2.8125 8.4375H9.9375V7.3125H2.8125V8.4375ZM2.8125 5.52394H9.9375V4.39913H2.8125V5.52394ZM6.77813 1.97531C6.88438 1.86906 6.9375 1.73469 6.9375 1.57219C6.9375 1.40969 6.88438 1.27531 6.77813 1.16906C6.67188 1.06281 6.5375 1.00969 6.375 1.00969C6.2125 1.00969 6.07813 1.06281 5.97188 1.16906C5.86563 1.27531 5.8125 1.40969 5.8125 1.57219C5.8125 1.73469 5.86563 1.86906 5.97188 1.97531C6.07813 2.08156 6.2125 2.13469 6.375 2.13469C6.5375 2.13469 6.67188 2.08156 6.77813 1.97531Z" fill="#F75819"/>
                </svg>
              </div>
              BEFORE YOU COME IN
            </div>
          </div>
          <div className="prep-list">
            {allPreparations.map((prep, index) => (
              <div key={index} className="prep-item">
                <div className="prep-num">{index + 1}</div>
                <div className="prep-text">{prep}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SECTION 2: Assessment Package */}
      <div id="tests-section" className="section">
        <div className="section-header">
            <div className="section-title">
              <div className="section-icon">
                <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.00712509 15.8366V15.2741C0.00712509 13.5317 0.364813 12.1476 1.08019 11.1217C1.79556 10.0957 2.77394 9.02794 4.01531 7.91831C2.76919 6.80869 1.78844 5.74088 1.07306 4.71488C0.357688 3.689 0 2.30487 0 0.5625V0H1.125V0.5625C1.125 0.75775 1.1305 0.942375 1.1415 1.11637C1.1525 1.2905 1.1715 1.46169 1.1985 1.62994H8.55863C8.5855 1.46169 8.6045 1.2905 8.61563 1.11637C8.62663 0.942375 8.63213 0.75775 8.63213 0.5625V0H9.75713V0.5625C9.75713 2.30487 9.39944 3.689 8.68406 4.71488C7.96869 5.74088 6.99031 6.80869 5.74894 7.91831C6.99031 9.02306 7.96869 10.0896 8.68406 11.118C9.39944 12.1464 9.75713 13.5317 9.75713 15.2741V15.8366H8.63213V15.2741C8.63213 15.0789 8.62663 14.8942 8.61563 14.7202C8.6045 14.5461 8.5855 14.3749 8.55863 14.2067H1.20563C1.17875 14.3749 1.15975 14.5461 1.14863 14.7202C1.13763 14.8942 1.13213 15.0789 1.13213 15.2741V15.8366H0.00712509ZM2.32781 4.54331H7.42931C7.62544 4.27219 7.7985 3.99262 7.9485 3.70463C8.0985 3.41663 8.22587 3.10006 8.33063 2.75494H1.42631C1.53594 3.09244 1.66337 3.40588 1.80862 3.69525C1.95375 3.98463 2.12681 4.26731 2.32781 4.54331ZM4.88213 7.18125C5.17538 6.92075 5.45638 6.665 5.72513 6.414C5.99388 6.163 6.24656 5.91444 6.48319 5.66831H3.27394C3.51044 5.91444 3.76431 6.163 4.03556 6.414C4.30669 6.665 4.58888 6.92075 4.88213 7.18125ZM3.28106 10.1683H6.48319C6.24656 9.92219 5.99388 9.67362 5.72513 9.42262C5.45638 9.17162 5.17538 8.91587 4.88213 8.65537C4.88213 8.91587 4.30788 9.17162 4.03913 9.42262C3.77038 9.67362 3.51769 9.92219 3.28106 10.1683ZM1.43362 13.0817H8.33063C8.221 12.7442 8.09363 12.4307 7.9485 12.1414C7.80325 11.852 7.63019 11.5693 7.42931 11.2933H2.33494C2.13406 11.5693 1.95981 11.8501 1.81219 12.1356C1.66456 12.4212 1.53837 12.7366 1.43362 13.0817Z" fill="#F75819"/>
                </svg>
              </div>
              YOUR TESTS AND PROTOCOLS
            </div>
        </div>
        <div className="tests-body">
          {allTests && allTests.map((test, index) => (
            <div key={index} className="test-row">
              <div className="test-left">
                <div className="test-dot"></div>
                <div className="test-name">{test.name}</div>
              </div>
              <div className="test-price">₹{parseFloat(test.sale_price).toLocaleString('en-IN')}</div>
            </div>
          ))}
        </div>
        <div className="tests-total">
          <div className="total-label">Total Amount</div>
          <div className="total-amount"><sup>₹</sup>{totalAmount.toLocaleString('en-IN')}</div>
        </div>
      </div>

      {/* FOOTER ACTIONS */}
      <div className="footer-actions">
        {!hasPaymentId ? (
          <>
            {/* <button 
              className="btn-primary"
              onClick={initiatePayment}
              disabled={paymentLoading}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
              {paymentLoading ? 'Processing...' : `Pay Now · ₹${totalAmount.toLocaleString('en-IN')}`}
            </button> */}
            <div style={{background:'linear-gradient(135deg,#f0faf4 0%,#e8f5ee 100%)',border:'1px solid #b7dfc8',borderRadius:'12px',padding:'14px 16px',textAlign:'center',marginBottom:'4px'}}>
              <div style={{fontSize:'12px',color:'#2d7a50',fontWeight:'600',marginBottom:'6px',letterSpacing:'0.01em'}}>🎁 A little gift from us</div>
              <div style={{fontSize:'12px',color:'#3d5a4a',lineHeight:'1.6'}}>We've applied a special discount to your protocol, a small thank-you for choosing Vital Insights.</div>
              <div style={{fontSize:'12px',color:'#3d5a4a',lineHeight:'1.6',marginTop:'6px'}}>To complete your booking, payment can be made directly at the branch. Our team will be ready to assist you when you arrive.</div>
            </div>
            <button 
              className="btn-secondary" 
              onClick={() => {
                const startDate = new Date(primaryAppt.slot_start_time);
                const endDate = new Date(startDate.getTime() + 2*60*60*1000);
                
                const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                
                if (isMobile) {
                  const startTime = startDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                  const endTime = endDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                  const title = 'Vital Insights Assessment';
                  const description = 'Your Vital Insights assessment appointment';
                  
                  const icsContent = `BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//Vital Insights//EN\nBEGIN:VEVENT\nDTSTART:${startTime}\nDTEND:${endTime}\nSUMMARY:${title}\nDESCRIPTION:${description}\nEND:VEVENT\nEND:VCALENDAR`;
                  const blob = new Blob([icsContent], { type: 'text/calendar' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'vital-insights-appointment.ics';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                } else {
                  const startTime = startDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                  const endTime = endDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
                  const title = encodeURIComponent('Vital Insights Assessment');
                  const description = encodeURIComponent('Your Vital Insights assessment appointment');
                  
                  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startTime}/${endTime}&details=${description}`;
                  window.open(googleCalendarUrl, '_blank');
                }
              }}
              style={{fontSize:'13px',gap:'6px',textDecoration:'none'}}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              Add to Calendar
            </button>
            <div style={{textAlign:'center',fontSize:'11.5px',color:'var(--ink-3)',marginTop:'-2px'}}>Reports available after your assessment on {day} {month}</div>
            <div className="secure-note">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              Payments are 100% secure &amp; encrypted
            </div>
          </>
        ) : (
          <div className="payment-status">
            <div className="status-icon">✓</div>
            <div className="status-text">
              <div className="status-title">PAID</div>
              <div className="status-amount">₹{totalAmount.toLocaleString('en-IN')}</div>
            </div>
          </div>
        )}
      </div>

      {/* REPORTS SECTION */}
      {appointments.some(apt => apt.report_urls && apt.report_urls.length > 0) && (
        <div id="reports-section" className="section" style={{margin:'16px 16px 32px'}}>
          <div className="section-header">
            <div className="section-title">
              <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.35581 14.25C0.981812 14.25 0.662313 14.1176 0.397313 13.8527C0.132438 13.5877 0 13.2682 0 12.8942V2.85581C0 2.48181 0.132438 2.16231 0.397313 1.89731C0.662313 1.63244 0.981812 1.5 1.35581 1.5H4.69331C4.74044 1.08363 4.92219 0.729562 5.23856 0.437812C5.55481 0.145937 5.93363 0 6.375 0C6.82113 0 7.20238 0.145937 7.51875 0.437812C7.83513 0.729562 8.01444 1.08363 8.05669 1.5H11.3942C11.7682 1.5 12.0877 1.63244 12.3527 1.89731C12.6176 2.16231 12.75 2.48181 12.75 2.85581V12.8942C12.75 13.2682 12.6176 13.5877 12.3527 13.8527C12.0877 14.1176 11.7682 14.25 11.3942 14.25H1.35581ZM1.35581 13.125H11.3942C11.4519 13.125 11.5048 13.1009 11.5528 13.0528C11.6009 13.0048 11.625 12.9519 11.625 12.8942V2.85581C11.625 2.79806 11.6009 2.74519 11.5528 2.69719C11.5048 2.64906 11.4519 2.625 11.3942 2.625H1.35581C1.29806 2.625 1.24519 2.64906 1.19719 2.69719C1.14906 2.74519 1.125 2.79806 1.125 2.85581V12.8942C1.125 12.9519 1.14906 13.0048 1.19719 13.0528C1.24519 13.1009 1.29806 13.125 1.35581 13.125ZM2.8125 11.3509H7.6875V10.2261H2.8125V11.3509ZM2.8125 8.4375H9.9375V7.3125H2.8125V8.4375ZM2.8125 5.52394H9.9375V4.39913H2.8125V5.52394ZM6.77813 1.97531C6.88438 1.86906 6.9375 1.73469 6.9375 1.57219C6.9375 1.40969 6.88438 1.27531 6.77813 1.16906C6.67188 1.06281 6.5375 1.00969 6.375 1.00969C6.2125 1.00969 6.07813 1.06281 5.97188 1.16906C5.86563 1.27531 5.8125 1.40969 5.8125 1.57219C5.8125 1.73469 5.86563 1.86906 5.97188 1.97531C6.07813 2.08156 6.2125 2.13469 6.375 2.13469C6.5375 2.13469 6.67188 2.08156 6.77813 1.97531Z" fill="#F75819"/>
                </svg>
              YOUR REPORTS
            </div>
          </div>
          <div style={{padding:'0'}}>
            {appointments.map((apt, aptIndex) => 
              apt.report_urls && apt.report_urls.length > 0 && (
                <div key={aptIndex}>
                  {apt.report_urls.map((report, reportIndex) => (
                    <a
                      key={reportIndex}
                      href={report.report_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display:'flex',
                        alignItems:'center',
                        justifyContent:'space-between',
                        padding:'13px 18px',
                        borderBottom:'1px solid var(--line)',
                        textDecoration:'none',
                        color:'inherit',
                        transition:'background 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--brand-light)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{display:'flex',alignItems:'center',gap:'12px',flex:'1',minWidth:'0'}}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand)" strokeWidth="2.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        <div style={{minWidth:'0'}}>
                          <div style={{fontSize:'13.5px',fontWeight:'500',color:'var(--ink)',marginBottom:'2px'}}>
                            {
                              report.report_type === 'bloodReport' ? 'Blood Report' :
                              report.report_type === 'dexaReport' ? 'DEXA Report' :
                              report.report_type === 'vo2maxReport' ? 'VO2 Max Report' :
                              report.report_type === 'summaryReport' ? 'Summary Report' :
                              report.report_type === 'strengthReport' ? 'Strength Report' :
                              report.report_type === 'alzheimerReport' ? 'Alzheimer Report' :
                              report.report_type === 'fatLossReport' ? 'Fat Loss Report' :
                              'Report'
                            }
                          </div>
                          <div style={{fontSize:'11px',color:'var(--ink-3)'}}>
                            {new Date(report.created_at).toLocaleDateString('en-IN')}
                          </div>
                        </div>
                      </div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--brand)" strokeWidth="2.5" strokeLinecap="round"><path d="M7 17L17 7M17 7H7M17 7V17"/></svg>
                    </a>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderTokenPage;
